import { Box, CircularProgress, Fab, Typography, useTheme } from '@mui/material';
import { useGetCampaignQuery } from '@service';
import { GetCampaignResponse, UserNotification } from '@types';
import dayjs from 'dayjs';
import { ReactNode, memo, useMemo } from 'react';

export interface BaseCampaignNotificationProps {
  notification: UserNotification;
  onNavigate: (id: string) => void;
  campaignId: string | null;
  notificationType: string;
  renderMessage: (campaignName: string) => string;
  actionIcon: ReactNode;
  onAction: (campaignData?: GetCampaignResponse | null) => void;
  showActionButton?: (data: GetCampaignResponse | null) => boolean;
}

const BaseCampaignNotification = ({
  notification,
  campaignId,
  notificationType,
  renderMessage,
  actionIcon,
  onAction,
  showActionButton = () => true
}: BaseCampaignNotificationProps) => {
  const theme = useTheme();

  const { data, error, isLoading } = useGetCampaignQuery(campaignId as string, {
    skip: !campaignId
  });

  const createdAtRelative = useMemo(() => {
    return dayjs(notification.created_at).fromNow();
  }, [notification.created_at]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          p: 2,
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // don't show erroneous notifications
  if (error || !data) return null;

  const shouldShowAction = showActionButton(data);

  return (
    <Box
      sx={{
        display: 'flex',
        p: 2,
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: 'text.secondary',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '166%',
            letterSpacing: '0px'
          }}
        >
          {notificationType}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '166%',
              letterSpacing: '0px'
            }}
          >
            {createdAtRelative}
          </Typography>
          {notification.seen ? (
            <Box sx={{ width: 8 }} />
          ) : (
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            letterSpacing: '0px',
            lineHeight: '143%',
            flex: 1
          }}
        >
          {renderMessage(data.name)}
        </Typography>
        {shouldShowAction && (
          <Fab size="small" color="primary" sx={{ color: 'white' }} onClick={() => onAction(data)}>
            {actionIcon}
          </Fab>
        )}
      </Box>
    </Box>
  );
};

export default memo(BaseCampaignNotification);
